// @ts-check

class CalculatorBlock {
    constructor(debug = false) {
        this.debug = debug;
        this.calculatorResizeHandler = null;
        this.Init();
    }

    Init() {
        this.DisableNumberInputWheel();
    }

    DisableNumberInputWheel() {
        const numberInputs = document.querySelectorAll('input[type="number"]');

        numberInputs.forEach(input => {
            input.addEventListener('wheel', event => {
                event.preventDefault();
            }, { passive: false });
        });
    }

    DebugInfo(message, ...optionalParams) {
        if (this.debug) {
            console.log(`[CalculatorBlock]`, message, ...optionalParams);
        }
    }

    DisableSmoothScroll() {
        const htmlElement = document.documentElement;
        const navSelector = '[data-element-type="nav"]';

        htmlElement.classList.remove('scroll-smooth');

        const updateScrollPaddingTop = () => {
            const navElement = document.querySelector(navSelector);
            const navHeight = navElement ? Math.ceil(navElement.getBoundingClientRect().height) + 100 : 0;

            htmlElement.style.setProperty('scroll-padding-top', `${navHeight}px`);
        };

        if (this.calculatorResizeHandler) {
            window.removeEventListener('resize', this.calculatorResizeHandler);
        }

        this.calculatorResizeHandler = () => {
            updateScrollPaddingTop();
        };

        updateScrollPaddingTop();
    }

    EnableSmoothScroll() {
        const htmlElement = document.documentElement;

        if (this.calculatorResizeHandler) {
            window.removeEventListener('resize', this.calculatorResizeHandler);
            this.calculatorResizeHandler = null;
        }

        htmlElement.classList.add('scroll-smooth');
        htmlElement.style.removeProperty('scroll-padding-top');
    }

    GetCurrentPage() {
        const currentPage = document.querySelector('[data-element-type="calculator-steps-container"]:not(.hidden)')?.dataset.page;
        return Number.parseInt(currentPage ?? '1', 10) || 1;
    }

    ShowStep(currentPage, targetPage) {
        const currentActivePage = this.GetCurrentPage();
        const nextTargetPage = targetPage === undefined ? Number.parseInt(currentPage, 10) : Number.parseInt(targetPage, 10);

        if (Number.isNaN(nextTargetPage)) {
            return;
        }

        if (nextTargetPage > currentActivePage) {
            for (let pageToValidate = currentActivePage; pageToValidate < nextTargetPage; pageToValidate++) {
                const isValid = this.ValidateStep(pageToValidate);
                const navigationPage = document.querySelector(`[data-element-type="calculator-navigation"] li[data-page="${pageToValidate}"]`);

                if (!isValid) {
                    navigationPage?.setAttribute('data-valid', 'false');
                    return;
                }

                navigationPage?.setAttribute('data-valid', 'true');
            }
        }

        const stepsContainers = document.querySelectorAll('[data-element-type="calculator-steps-container"]');
        stepsContainers.forEach(container => {
            container.classList.add('hidden');
        });

        const targetContainer = document.querySelector(`[data-element-type="calculator-steps-container"][data-page="${nextTargetPage}"]`);

        if (targetContainer) {
            targetContainer.classList.remove('hidden');

            this.UpdateNavigation();

            targetContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    UpdateNavigation(setValidPage) {
        const currentPage = this.GetCurrentPage();
        const navigationItems = document.querySelectorAll('[data-element-type="calculator-navigation"] li');

        navigationItems.forEach(btn => {
            btn.setAttribute('data-active', 'false');
        });

        const activeNavItem = document.querySelector(`[data-element-type="calculator-navigation"] li[data-page="${currentPage}"]`);

        if (activeNavItem) {
            activeNavItem.setAttribute('data-active', 'true');
        }
    }

    ValidateStep(pageNumber) {
        const targetContainer = document.querySelector(`[data-element-type="calculator-steps-container"][data-page="${pageNumber}"]`);

        if (targetContainer) {
            const fields = targetContainer.querySelectorAll('input:not([type="checkbox"]), select, textarea');
            const processedRadioGroups = new Set();

            for (const field of fields) {
                let errorMessage = '';

                if (field.type === 'radio') {
                    const groupName = field.name;

                    if (processedRadioGroups.has(groupName)) {
                        continue;
                    }

                    processedRadioGroups.add(groupName);

                    const radioGroup = targetContainer.querySelectorAll(`input[type="radio"][name="${groupName}"]`);
                    const errorContainer = document.getElementById(`${groupName}-error`);

                    if (field.required) {
                        const isSelected = Array.from(radioGroup).some(radio => radio.checked);

                        if (!isSelected) {
                            errorMessage = field.dataset.msgRequired || 'Please select an option.';
                        }
                    }

                    radioGroup.forEach(radio => radio.setCustomValidity(errorMessage));

                    this.UpdateValidationErrorMessage(errorContainer, errorMessage, Array.from(radioGroup));

                    if (errorMessage) {
                        this.DisableSmoothScroll();
                        field.focus({ preventScroll: true });
                        field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

                        if (!errorContainer) {
                            field.reportValidity();
                        }

                        setTimeout(() => {
                            this.EnableSmoothScroll();
                        }, 50);
                        return false;
                    }

                    continue;
                }

                const errorContainerId = (field.name || field.id) + '-error';
                const errorContainer = errorContainerId ? document.getElementById(errorContainerId) : null;

                field.setCustomValidity('');

                if (field.validity.valueMissing) {
                    errorMessage = field.dataset.msgRequired || 'This field is required.';
                }
                else if (field.type === 'number' && field.value !== '') {
                    const value = field.valueAsNumber;
                    const minAttribute = field.getAttribute('min');
                    const maxAttribute = field.getAttribute('max');
                    const min = minAttribute !== null ? Number.parseFloat(minAttribute) : Number.NaN;
                    const max = maxAttribute !== null ? Number.parseFloat(maxAttribute) : Number.NaN;

                    if (!Number.isNaN(min) && value < min) {
                        errorMessage = field.dataset.msgMin || `Please enter a value greater than or equal to ${min}.`;
                    } else if (!Number.isNaN(max) && value > max) {
                        errorMessage = field.dataset.msgMax || `Please enter a value less than or equal to ${max}.`;
                    }
                }

                this.UpdateValidationErrorMessage(errorContainer, errorMessage, [field]);

                if (errorMessage) {
                    field.setCustomValidity(errorMessage);
                    this.DisableSmoothScroll();
                    field.focus({ preventScroll: true });
                    field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

                    if (!errorContainer) {
                        field.reportValidity();
                    }

                    setTimeout(() => {
                        this.EnableSmoothScroll();
                    }, 50);
                    return false;
                }
            }

            const checkboxes = targetContainer.querySelectorAll('input[type="checkbox"]');
            const processedNames = new Set();

            for (const cb of checkboxes) {
                const groupName = cb.name;

                if (processedNames.has(groupName)) {
                    continue;
                }

                processedNames.add(groupName);

                const group = targetContainer.querySelectorAll(`input[type="checkbox"][name="${groupName}"]`);
                const checkedCount = Array.from(group).filter(el => el.checked).length;
                const min = Number.parseInt(cb.dataset.min ?? '0', 10) || 0;
                const max = Number.parseInt(cb.dataset.max ?? '', 10) || Infinity;
                const errorContainerId = `${groupName}-error`;
                const errorContainer = document.getElementById(errorContainerId);

                let errorMessage = '';

                if (checkedCount < min) {
                    errorMessage = cb.dataset.msgMin || `Please select at least ${min} option(s).`;
                } else if (checkedCount > max) {
                    errorMessage = cb.dataset.msgMax || `Please select no more than ${max} option(s).`;
                }

                group.forEach(el => el.setCustomValidity(errorMessage));

                this.UpdateValidationErrorMessage(errorContainer, errorMessage, Array.from(group));

                if (errorMessage) {
                    this.DisableSmoothScroll();
                    cb.focus({ preventScroll: true });
                    cb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

                    if (!errorContainer) {
                        cb.reportValidity();
                    }

                    setTimeout(() => {
                        this.EnableSmoothScroll();
                    }, 50);
                    return false;
                }
            }
        }

        return true;
    }

    UpdateValidationErrorMessage(errorContainer, errorMessage, controls = []) {
        if (errorContainer?.dataset.elementType !== 'calculator-error-message') {
            return;
        }

        const errorMessageElement = errorContainer.querySelector('div');

        if (errorMessageElement) {
            errorMessageElement.textContent = errorMessage;
        }

        errorContainer.classList.toggle('hidden', !errorMessage);
        errorContainer.classList.toggle('grid', !!errorMessage);

        const describedById = errorContainer.id;

        for (const control of controls) {
            if (!control) {
                continue;
            }

            control.setAttribute('aria-invalid', errorMessage ? 'true' : 'false');

            if (!describedById) {
                continue;
            }

            const currentDescriptionIds = (control.getAttribute('aria-describedby') ?? '')
                .split(/\s+/)
                .filter(Boolean)
                .filter(id => id !== describedById);

            if (errorMessage) {
                currentDescriptionIds.push(describedById);
            }

            if (currentDescriptionIds.length > 0) {
                control.setAttribute('aria-describedby', Array.from(new Set(currentDescriptionIds)).join(' '));
            } else {
                control.removeAttribute('aria-describedby');
            }
        }
    }

    IsRadioGroupSelected(groupName, validValues) {
        const radioGroup = document.querySelectorAll(`input[name="${groupName}"]`);

        if (!radioGroup || radioGroup.length === 0) {
            return false;
        }

        const checkedRadio = Array.from(radioGroup).find(el => el.checked);
        const radioValue = checkedRadio ? checkedRadio.value : null;

        return Array.isArray(validValues) && validValues.includes(radioValue);
    }

    IsCheckboxSelected(groupName, validValues) {
        const checkboxGroup = document.querySelectorAll(`input[name="${groupName}"]`);

        if (!checkboxGroup || checkboxGroup.length === 0) {
            return false;
        }

        return Array.from(checkboxGroup).some(el => el.checked && Array.isArray(validValues) && validValues.includes(el.value));
    }

    IsDropDownSelected(id, validValues) {
        const dropDown = document.getElementById(id);

        if (!dropDown) {
            return false;
        }

        const selectedValue = dropDown.value;
        return Array.isArray(validValues) && validValues.includes(selectedValue);
    }

    ShowHideElement(show, elementId) {
        var element = document.getElementById(elementId);
        if (!element) return;

        if (show) {
            element.classList.remove("hidden");
            element.classList.add("block");
        } else {
            element.classList.add("hidden");
            element.classList.remove("block");
        }
    }
}


// Expose the class globally
// @ts-expect-error - Is not defined in window by default, but we are attaching it here for global access
window.CalculatorBlock = CalculatorBlock;

// @ts-expect-error - Is not defined in window by default, but we are attaching it here for global access
window.Calculator = new CalculatorBlock();
